import React from 'react';
import { FaFacebookF, FaLinkedinIn, FaInstagram } from 'react-icons/fa';
import { useTenantConfigQuery } from '../api/queries';

export default function Sidebar() {
  const { data: tenantConfig, isLoading: isPageLoading } = useTenantConfigQuery();

  if (isPageLoading) {
    return (
      <div className="content-left" style={{ overflow: 'hidden' }}>
        {/* Social icons skeleton */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
          {[0,1,2].map(i => <div key={i} style={skeletonStyles.circle} />)}
        </div>

        {/* Logo box skeleton */}
        <div style={skeletonStyles.logoBox} />

        {/* Heading skeleton */}
        <div style={{ ...skeletonStyles.line, width: '72%', height: 20, marginTop: 28 }} />

        {/* Description skeleton lines */}
        <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ ...skeletonStyles.line, width: '100%' }} />
          <div style={{ ...skeletonStyles.line, width: '88%' }} />
          <div style={{ ...skeletonStyles.line, width: '94%' }} />
          <div style={{ ...skeletonStyles.line, width: '78%', marginTop: 6 }} />
          <div style={{ ...skeletonStyles.line, width: '92%' }} />
          <div style={{ ...skeletonStyles.line, width: '65%' }} />
        </div>

        <style>{`
          @keyframes shimmer {
            0%   { background-position: -600px 0; }
            100% { background-position:  600px 0; }
          }
        `}</style>
      </div>
    );
  }

  //  DESCRIPTION SPLITTER (your logic)
  const getSplitDescription = (text) => {
    if (!text) return [];

    let parts = text.split('\n').filter(p => p.trim() !== '');

    if (parts.length === 1) {
      const sentences = text.match(/[^.!?]+[.!?]+/g) || [];
      if (sentences.length >= 2) {
        const mid = Math.ceil(sentences.length / 2);
        parts = [
          sentences.slice(0, mid).join(' ').trim(),
          sentences.slice(mid).join(' ').trim(),
        ].filter(p => p.length > 0);
      } else {
        const mid = Math.ceil(text.length / 2);
        const spaceIndex = text.indexOf(' ', mid);
        const splitAt = spaceIndex !== -1 ? spaceIndex : mid;
        parts = [
          text.slice(0, splitAt).trim(),
          text.slice(splitAt).trim(),
        ].filter(p => p.length > 0);
      }
    }

    return parts;
  };

  const descriptionParts = getSplitDescription(tenantConfig?.description);

  return (
    <div className="content-left">

      {/* Social Links */}
      <div className="social-links">
        <a href="https://facebook.com" target="_blank" rel="noreferrer">
          <FaFacebookF color="#fff" size={18} />
        </a>
        <a href="https://linkedin.com" target="_blank" rel="noreferrer">
          <FaLinkedinIn color="#fff" size={18} />
        </a>
        <a href="https://instagram.com" target="_blank" rel="noreferrer">
          <FaInstagram color="#fff" size={18} />
        </a>
      </div>

      {/* Logo */}
      <div className="logo-box" style={styles.logoBox}>
        {tenantConfig?.logo_url ? (
          <img
            src={tenantConfig.logo_url}
            alt="Company Logo"
            style={styles.logoImg}
          />
        ) : (
          <span style={styles.logoFallback}>
            {tenantConfig?.company_name || 'Company'}
          </span>
        )}
      </div>

      <h2>Become a part of our team</h2>

      {/* Description */}
      <div style={{ marginTop: 28 }}>
        {descriptionParts.length > 0 ? (
          descriptionParts.map((para, index) => (
            <p key={index} className="description-full" style={styles.descPara}>
              {para}
            </p>
          ))
        ) : (
          <>
            <p className="description-full" style={styles.descPara}>
              We provide professional services focused on quality, efficiency, and customer satisfaction.
              Our goal is to deliver reliable solutions that help businesses grow and succeed.
            </p>
            <p className="description-full" style={styles.descPara}>
              Our team is dedicated to maintaining high standards, improving performance,
              and creating long-term value through consistent and effective work.
            </p>
          </>
        )}
      </div>

      <div className="copy-text">
        ©{new Date().getFullYear()} Powered by{' '}
        <a href="https://fusionlogics.com" target="_blank" rel="noreferrer">
          Fusion Logics
        </a>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .content-left {
            padding: 15px;
            text-align: center;
          }
          h2 {
            font-size: 18px;
          }
          .description-full {
            font-size: 15px !important;
          }
        }
      `}</style>
    </div>
  );
}

const styles = {
  logoBox: {
    width: '100%',
    maxWidth: 270,
    height: 240,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 70,
    marginBottom: 24,
    backgroundColor: '#ffffff',
    borderRadius: 10,
    padding: 15,
    boxSizing: 'border-box',
    marginLeft: 'auto',
    marginRight: 'auto',
  },

  logoImg: {
    width: '100%',
    height: '100%',
    maxWidth: 180,
    maxHeight: 180,
    objectFit: 'contain',
  },


  logoFallback: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#333',
    textAlign: 'center',
  },

  descPara: {
    fontSize: '15px',
    lineHeight: '1.65',
    marginBottom: '16px',
    opacity: 0.92,
  },
};

const SHIMMER = {
  background: 'linear-gradient(90deg, rgba(255,255,255,0.07) 25%, rgba(255,255,255,0.18) 50%, rgba(255,255,255,0.07) 75%)',
  backgroundSize: '600px 100%',
  animation: 'shimmer 1.6s ease-in-out infinite',
  borderRadius: 6,
};

const skeletonStyles = {
  circle: {
    ...SHIMMER,
    width: 34,
    height: 34,
    borderRadius: '50%',
  },
  logoBox: {
    ...SHIMMER,
    width: '100%',
    maxWidth: 270,
    height: 240,
    borderRadius: 10,
    marginTop: 70,
    marginBottom: 24,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  line: {
    ...SHIMMER,
    height: 14,
  },
};